/**
 * t536u: Settings › People › Moderation (the reports queue; t614u: was 'Reports') and Deletion requests, and Settings › My account › Delete my
 * account. Desktop and phone web share these panels (the phone opens them full screen from Me); the iOS app has the
 * same screens with the same wording (packages/shared/src/moderation.ts). The API checks every permission again.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import {
  ACCOUNT_DELETE_TEXT as AD, DELETION_QUEUE_TEXT as DQ, MEMBER_DELETE_TEXT, MODERATION_TEXT as M, REPORT_STATUS_LABEL, accountDeleteConfirmMatches,
  deleteNameMatches, reportReasonLabel, type ContentReport, type DeletionRequestRow,
} from '@tradeoutlet/shared';
import { Button, ConfirmDialog, EmptyState, ErrorBox, Input, Modal, Spinner, Tabs, Textarea, ask } from '@/components/ui';
import { DeleteMemberBody, DeleteMemberButtons } from '@/features/people/users/DeleteMember';
import { api, errorMessage } from '@/lib/api';
import { DATE_FORMATS, formatDate, fullDateTime } from '@/lib/format';
import { useWorkspaceId } from '@/lib/hooks';
import { usePermissions } from '@/lib/permissions';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';

const card = 'rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900';
const refreshAll = (ws: string | null) => { for (const k of ['reports', 'reports-summary', 'deletion-requests', 'members-all', 'person']) void queryClient.invalidateQueries({ queryKey: [k, ws] }); };

/** Open reports and deletion requests for the banner and badges (owners, admins and report reviewers). */
export function useModerationSummary(enabled = true) {
  const ws = useWorkspaceId();
  const mine = usePermissions().data;
  const can = Boolean(mine && (mine.workspace.includes('moderation.manage') || mine.workspace.includes('admin.users.manage')));
  return useQuery({ queryKey: ['reports-summary', ws], queryFn: () => api.reportsSummary(ws!), enabled: Boolean(ws) && can && enabled, staleTime: 60_000 });
}

/** Shown to owners on Settings, Users and Me while deletion requests wait (more strongly after 7 days). */
export function DeletionRequestsBanner({ className }: { className?: string }) {
  const s = useModerationSummary();
  const n = s.data?.deletion_requests ?? 0;
  if (!n) return null;
  const overdue = (s.data?.deletion_overdue ?? 0) > 0;
  return (
    <div role="status" data-testid="deletion-requests-banner" className={clsx('flex items-center gap-3 rounded-xl border px-4 py-3 text-sm', overdue
      ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100'
      : 'border-slate-200 bg-slate-50 text-slate-800 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100', className)}>
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
      <span className="flex-1">{DQ.banner(n)}</span>
      <Link to="/settings/people/deletion-requests" className="font-semibold underline" data-testid="deletion-requests-review">{DQ.review}</Link>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ Reports
export function ReportsPanel() {
  const ws = useWorkspaceId();
  const [tab, setTab] = useState<'open' | 'closed'>('open');
  const q = useQuery({ queryKey: ['reports', ws, tab], queryFn: () => api.reports(ws!, tab), enabled: Boolean(ws) });
  return (
    <div className="space-y-3" data-testid="reports-panel">
      <Tabs label="Moderation" value={tab} onChange={setTab} tabs={[{ id: 'open', label: M.open }, { id: 'closed', label: M.closed }]} />
      {q.isLoading ? <Spinner /> : q.error ? <ErrorBox error={q.error} onRetry={() => void q.refetch()} />
        : !q.data?.reports.length ? <EmptyState title={tab === 'open' ? 'No open reports' : 'Nothing closed yet'}>{tab === 'open' ? M.empty : M.emptyClosed}</EmptyState>
        : <ul className="space-y-3">{q.data.reports.map(r => <li key={r.id}><ReportCard r={r} /></li>)}</ul>}
    </div>
  );
}

function ReportCard({ r }: { r: ContentReport }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const nav = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const act = async (key: string, fn: () => Promise<unknown>, done?: string) => {
    setBusy(key);
    try { await fn(); if (done) toast(done, 'success'); refreshAll(ws); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(null); }
  };
  const open = r.status === 'open';
  const who = r.reported.display_name ?? 'Deleted user';
  return (
    <article className={card} data-testid="report-card">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">{r.target_type === 'message' ? `Message from ${who}` : `About ${who}`}</span>
        <span className="text-slate-500" data-testid="report-reason">· {reportReasonLabel(r.reason)}</span>
        {!r.reported.active && r.reported.user_id && <span className="text-slate-500">· Deactivated</span>}
        {!open && <span className="ml-auto text-slate-500">{REPORT_STATUS_LABEL[r.status]}</span>}
      </div>
      <p className="mt-1 text-xs text-slate-500" data-testid="report-meta">
        {M.reportedBy} {r.reporter.display_name ?? 'Deleted user'} · <time dateTime={r.created_at}>{fullDateTime(r.created_at)}</time>
        {r.message && <> · {r.message.where}</>}
        {!open && r.resolved_at && <> · {REPORT_STATUS_LABEL[r.status]} by {r.resolved_by_name ?? 'someone'} {formatDate(r.resolved_at, 'd LLL')}</>}
      </p>
      {r.message && (
        <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-950" data-testid="report-context">
          {(r.message.context.length ? r.message.context : [{ id: r.message.id, sender_name: who, body: r.message.body || r.message.snapshot || '', created_at: r.message.created_at, deleted: r.message.deleted, reported: true }]).map(c => (
            <div key={c.id} className={clsx('rounded-md px-2 py-1 text-sm', c.reported && 'bg-amber-100/70 ring-1 ring-amber-300 dark:bg-amber-900/30 dark:ring-amber-700')} data-testid={c.reported ? 'report-context-reported' : undefined}>
              <span className="font-semibold">{c.sender_name ?? 'Deleted user'}</span> <span className="text-xs text-slate-500">{formatDate(c.created_at, DATE_FORMATS.dayTime)}</span>
              <div className="whitespace-pre-wrap break-words">{c.deleted ? <i className="text-slate-500">Deleted{c.reported && r.message?.snapshot ? `: “${r.message.snapshot}”` : ''}</i> : c.body}</div>
            </div>
          ))}
          {r.message.link && <Link to={r.message.link} className="mt-1 inline-flex items-center gap-1 px-2 text-xs font-medium underline" data-testid="report-open">{M.openInChat}<ExternalLink className="h-3 w-3" /></Link>}
        </div>
      )}
      {r.details && <p className="mt-2 whitespace-pre-wrap text-sm"><span className="font-medium">{M.details}:</span> {r.details}</p>}
      {open && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" loading={busy === 'dismiss'} onClick={() => void act('dismiss', () => api.dismissReport(ws!, r.id), M.dismissed)} data-testid="report-dismiss">{M.dismiss}</Button>
          {r.message && !r.message.deleted && <Button size="sm" variant="danger" loading={busy === 'delete'} data-testid="report-delete-message"
            onClick={async () => { if (await ask({ title: M.deleteMessage, body: M.deleteConfirm, confirmLabel: M.deleteMessage })) void act('delete', () => api.reportDeleteMessage(ws!, r.id), M.deleted); }}>{M.deleteMessage}</Button>}
          {r.reporter.user_id && <Button size="sm" variant="secondary" loading={busy === 'dm'} data-testid="report-message-reporter"
            onClick={() => void act('dm', async () => { const c = await api.messageReporter(ws!, r.id); nav(`/c/${c.id}`); })}>{M.messageReporter}</Button>}
          {r.reported.user_id && r.reported.active && <Button size="sm" variant="ghost" onClick={() => setDeactivating(true)} data-testid="report-deactivate">{M.deactivate}</Button>}
        </div>
      )}
      {/* The usual deactivate confirmation (Settings › Users), then the report closes as "Person deactivated". */}
      <ConfirmDialog open={deactivating} title={`Deactivate ${who}?`} confirmLabel="Deactivate" onCancel={() => setDeactivating(false)}
        onConfirm={() => { setDeactivating(false); void act('deactivate', async () => { await api.updateMember(ws!, r.reported.user_id!, { status: 'deactivated' }); await api.reportPersonDeactivated(ws!, r.id); }, M.deactivated); }}>
        They lose access immediately. Their history, shifts and documents are kept.
      </ConfirmDialog>
    </article>
  );
}

// ------------------------------------------------------------------------------------------------ Deletion requests (owners)
const daysSince = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

export function DeletionRequestsPanel() {
  const ws = useWorkspaceId();
  const q = useQuery({ queryKey: ['deletion-requests', ws], queryFn: () => api.deletionRequests(ws!), enabled: Boolean(ws) });
  const [approving, setApproving] = useState<DeletionRequestRow | null>(null);
  const [declining, setDeclining] = useState<DeletionRequestRow | null>(null);
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  const rows = q.data?.requests ?? [];
  const pending = rows.filter(r => r.status === 'pending');
  const past = rows.filter(r => r.status !== 'pending');
  return (
    <div className="space-y-4" data-testid="deletion-requests-panel">
      {!q.data?.can_decide && <p className="text-sm text-slate-500">{DQ.ownersOnly}</p>}
      <section>
        <h2 className="mb-2 text-sm font-semibold">{DQ.pending}</h2>
        {!pending.length ? <EmptyState title={DQ.empty} /> : (
          <ul className="space-y-2">{pending.map(r => (
            <li key={r.id} className={clsx(card, r.overdue && 'border-amber-300 dark:border-amber-700')} data-testid="deletion-request">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold">{r.display_name ?? 'Deleted user'}</span>
                {r.email && <span className="text-sm text-slate-500">{r.email}</span>}
                {/* t546u: "Asked today" repeated the date line below, so only say how long once it's a day or more */}
                {daysSince(r.requested_at) >= 1 && <span className={clsx('text-sm', r.overdue ? 'font-medium text-amber-800 dark:text-amber-200' : 'text-slate-500')}>· {DQ.waiting(daysSince(r.requested_at))}</span>}
              </div>
              <p className="mt-1 text-xs text-slate-500">Asked {fullDateTime(r.requested_at)}{r.active ? '' : ' · Deactivated'}</p>
              {q.data?.can_decide && <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="danger" onClick={() => setApproving(r)} data-testid="deletion-approve">{DQ.approve}</Button>
                <Button size="sm" variant="secondary" onClick={() => setDeclining(r)} data-testid="deletion-decline">{DQ.decline}</Button>
              </div>}
            </li>
          ))}</ul>
        )}
      </section>
      {past.length > 0 && <section>
        <h2 className="mb-2 text-sm font-semibold">{DQ.history}</h2>
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-800">{past.map(r => (
          <li key={r.id} className="px-4 py-2 text-sm" data-testid="deletion-request-past">
            <span className="font-medium">{r.display_name ?? 'Deleted user'}</span> · {r.status === 'approved' ? 'Approved' : r.status === 'declined' ? 'Declined' : 'Cancelled by them'}
            {r.decided_at && <> {formatDate(r.decided_at, 'd LLL yyyy')}{r.decided_by_name ? ` by ${r.decided_by_name}` : ''}</>}
            {r.decision_note && <span className="block text-slate-500">“{r.decision_note}”</span>}
          </li>
        ))}</ul>
      </section>}
      {approving && <ApproveDialog r={approving} onClose={() => setApproving(null)} />}
      {declining && <DeclineDialog r={declining} onClose={() => setDeclining(null)} />}
    </div>
  );
}

/** Approve = the existing "Delete permanently" (same wording and typed name); still-active people are deactivated first. */
function ApproveDialog({ r, onClose }: { r: DeletionRequestRow; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const name = r.display_name ?? '';
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = deleteNameMatches(typed, name);
  const run = async () => {
    if (!ws || !ok || busy) return;
    setBusy(true); setError(null);
    try { await api.approveDeletionRequest(ws, r.id, { confirm_name: typed }); toast(MEMBER_DELETE_TEXT.done, 'success'); refreshAll(ws); onClose(); }
    catch (e) { setError(errorMessage(e) || MEMBER_DELETE_TEXT.failed); } finally { setBusy(false); }
  };
  const s = { typed, setTyped, busy, error, ok, run, reset: () => { setTyped(''); setError(null); } };
  return (
    <Modal open onClose={() => !busy && onClose()} title={MEMBER_DELETE_TEXT.title(name)} footer={<DeleteMemberButtons s={s} onCancel={onClose} />}>
      {r.active && <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100" data-testid="deletion-deactivate-first">{DQ.deactivateFirst}</p>}
      <DeleteMemberBody name={name} s={s} />
    </Modal>
  );
}

function DeclineDialog({ r, onClose }: { r: DeletionRequestRow; onClose: () => void }) {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.declineDeletionRequest(ws!, r.id, { message: msg.trim() || undefined }); toast(DQ.declined, 'success'); refreshAll(ws); onClose(); }
    catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={() => !busy && onClose()} title={DQ.declineTitle(r.display_name ?? 'this person')}
      footer={<><Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button><Button onClick={() => void go()} loading={busy} data-testid="deletion-decline-confirm">{DQ.decline}</Button></>}>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">{DQ.declineLabel}</span>
        <Textarea rows={3} maxLength={1000} value={msg} onChange={e => setMsg(e.target.value)} data-testid="deletion-decline-message" />
        <span className="mt-1 block text-xs text-slate-500">{DQ.declineHint}</span>
      </label>
    </Modal>
  );
}

// ------------------------------------------------------------------------------------------------ Delete my account
export function DeleteAccountPanel() {
  const ws = useWorkspaceId();
  const toast = useUi(s => s.toast);
  const q = useQuery({ queryKey: ['my-deletion-request', ws], queryFn: () => api.myDeletionRequest(ws!), enabled: Boolean(ws) });
  const [step, setStep] = useState<'explain' | 'confirm'>('explain');
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data!;
  const set = (v: typeof d) => queryClient.setQueryData(['my-deletion-request', ws], v);
  const req = d.request;
  const submit = async () => {
    if (!ws || busy) return;
    setBusy(true); setError(null);
    try { set(await api.requestAccountDeletion(ws, { confirm: typed, password })); setPassword(''); setTyped(''); setStep('explain'); toast(AD.sent, 'success'); }
    catch (e) { setError(errorMessage(e) || AD.wrongPassword); } finally { setBusy(false); }
  };
  const cancelReq = async () => {
    if (!ws) return;
    try { set(await api.cancelAccountDeletion(ws)); toast(AD.cancelled, 'success'); } catch (e) { toast(errorMessage(e), 'error'); }
  };

  if (req?.status === 'pending') {
    return (
      <div className={card} data-testid="delete-account-requested">
        <p className="text-slate-600 dark:text-slate-300">{AD.requested(formatDate(req.requested_at, 'd LLL'))}</p>
        <p className="mt-1 text-sm text-slate-500">{AD.requestedNote}</p>
        <Button className="mt-3" size="sm" variant="secondary" onClick={() => void cancelReq()} data-testid="delete-account-cancel-request">{AD.cancelRequest}</Button>
      </div>
    );
  }
  const ok = accountDeleteConfirmMatches(typed, d.email);
  return (
    <div className="space-y-4" data-testid="delete-account-panel">
      {req?.status === 'declined' && (
        <div className="rounded-xl bg-slate-100 px-4 py-3 text-sm dark:bg-slate-800" data-testid="delete-account-declined">
          {AD.declined(formatDate(req.requested_at, 'd LLL'))}{req.decision_note && <span className="mt-1 block">“{req.decision_note}”</span>}
        </div>
      )}
      <div className={clsx(card, 'space-y-3 text-sm text-slate-700 dark:text-slate-200')}>
        <p>{AD.lead}</p>
        <div><p className="font-medium">{AD.whatHappens}</p><ul className="mt-1 list-disc space-y-0.5 pl-5">{AD.points.map(p => <li key={p}>{p}</li>)}</ul></div>
        <p>{AD.kept}</p>
        <p>{AD.elsewhere}</p>
        <p className="text-slate-500">{AD.review}</p>
      </div>
      {d.last_owner ? <p className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100" data-testid="delete-account-last-owner">{AD.lastOwner}</p>
        : !d.password_auth ? <p className="text-sm text-slate-600 dark:text-slate-300" data-testid="delete-account-no-password">{AD.noPassword}</p>
        : step === 'explain' ? <Button variant="danger" onClick={() => setStep('confirm')} data-testid="delete-account-continue">{AD.continue}</Button>
        : (
          <form className={clsx(card, 'space-y-3')} onSubmit={e => { e.preventDefault(); if (ok && password) void submit(); }} data-testid="delete-account-form">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{AD.confirmLabel(d.email)}</span>
              <Input value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} data-testid="delete-account-confirm" />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{AD.passwordLabel}</span>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" data-testid="delete-account-password" />
              <span className="mt-1 block text-xs text-slate-500">{AD.passwordHint}</span>
            </label>
            {error && <p role="alert" className="text-sm text-rose-700 dark:text-rose-300" data-testid="delete-account-error">{error}</p>}
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" onClick={() => { setStep('explain'); setError(null); }} disabled={busy}>{AD.cancel}</Button>
              <Button type="submit" variant="danger" disabled={!ok || !password} loading={busy} data-testid="delete-account-submit">{AD.submit}</Button>
            </div>
          </form>
        )}
    </div>
  );
}
